import type { GbpProfileSuggestion, GetMarketingGoogleRankingProfile200Data } from '@/api/generated/schemas';

/**
 * Pure helpers behind the Profile fixes tab's optimistic decisions and polling. Kept free of React Query so
 * every rule (what a queued card looks like, which ids a decision touches, how a failure is rolled back,
 * how often to re-read) is unit-tested directly.
 */

/** Approved but not yet picked up by the apply job ("Sending…"). */
export function isQueuedSuggestion(suggestion: GbpProfileSuggestion): boolean {
  return suggestion.status === 'SENT' && suggestion.sentAt === null;
}

/** The cached row as it looks right after approval: queued for Google. */
export function asQueued(suggestion: GbpProfileSuggestion, now: string): GbpProfileSuggestion {
  return { ...suggestion, status: 'SENT', sentAt: null, error: null, decidedAt: now };
}

/**
 * Suggestion ids a decision's mutation variables touch: skip = `id`, approve = `{ id, editedValue? }`,
 * approve-all = `id[]`.
 */
export function decisionIds(vars: unknown): string[] {
  if (typeof vars === 'string') return [vars];
  if (Array.isArray(vars)) return vars.filter((id): id is string => typeof id === 'string');
  if (vars && typeof vars === 'object' && 'id' in vars && typeof vars.id === 'string') return [vars.id];
  return [];
}

/**
 * Undo ONE failed decision without undoing others still in flight: the rows it touched come back from
 * `previous` (a skipped card reappears in its old place); every other row keeps its current (possibly
 * optimistic) state; rows that only exist now (a refetch landed meanwhile) are kept.
 */
export function restoreSuggestionRows(
  current: GbpProfileSuggestion[],
  previous: GbpProfileSuggestion[],
  ids: ReadonlySet<string>
): GbpProfileSuggestion[] {
  const now = new Map(current.map((row) => [row.id, row]));
  const before = new Set(previous.map((row) => row.id));
  return [
    ...previous.flatMap((row) => (ids.has(row.id) ? [row] : now.has(row.id) ? [now.get(row.id)!] : [])),
    ...current.filter((row) => !before.has(row.id)),
  ];
}

export const PROFILE_POLL_MS = {
  /** An approval waits for the apply job, or a "Check again" runs. */
  inFlight: 5_000,
  /** A queued approval older than `queuedFastWindowMs` (apply job slow / down): keep checking, gently. */
  slowQueue: 60_000,
  /** The very first read of a newly connected profile. */
  firstRead: 15_000,
  queuedFastWindowMs: 5 * 60_000,
  /** A queued approval this old is stuck (apply job down): stop polling; the next visit or "Check again" re-reads. */
  queuedStopAfterMs: 30 * 60_000,
} as const;

/**
 * How often to re-read the audit (database only): fast while an approval was just queued, slower once it has
 * waited a while, not at all after 30 minutes (the server fails it after 24h; an idle tab must not poll that
 * long), every 15s until the first read lands (unless that read failed — the daily refresh retries then) or while
 * an older score version is re-graded, otherwise never.
 */
export function profileRefetchInterval(data: GetMarketingGoogleRankingProfile200Data | undefined, nowMs: number): number | false {
  if (!data || data.availability !== 'READY') return false;
  const queued = data.suggestions.filter(isQueuedSuggestion);
  if (queued.length > 0) {
    const newest = Math.max(...queued.map((row) => (row.decidedAt ? Date.parse(row.decidedAt) : nowMs)));
    const waited = nowMs - newest;
    if (waited < PROFILE_POLL_MS.queuedFastWindowMs) return PROFILE_POLL_MS.inFlight;
    if (waited < PROFILE_POLL_MS.queuedStopAfterMs) return PROFILE_POLL_MS.slowQueue;
  }
  const firstReadPending = !data.snapshot || (data.snapshot.fetchedAt === null && data.snapshot.lastError === null);
  // A score of an older version is being graded again: the new one lands within a minute or two.
  return firstReadPending || data.snapshot?.rechecking ? PROFILE_POLL_MS.firstRead : false;
}
